import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Building2, Loader2, Plus, Calculator, AlertTriangle, FileText, Lock, Send, Pencil } from 'lucide-react';

// Plan C money is integer CENTS end to end (ADR 0003) — never parse it as dollars.
const money = (cents) => `$${(Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The last COMPLETE month. A period that has not finished is refused by
// `plan_c_invoice.readiness` because its submission count is still moving, so
// defaulting to the current month would offer a button that always fails.
const lastClosedPeriod = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const planLabel = (pl) => `${pl.name} — ${pl.platform_fee_cents === null ? 'negotiated' : `${money(pl.platform_fee_cents)}/site/mo`} · ${pl.included_submissions_per_site} included`;

// The commercial terms `organisations.UPDATABLE` accepts, other than the pricing
// model (which saves on its own — it decides whether patients are charged).
// Tax exempt is what lets an invoice issue with no sales-tax permit.
function TermsFields({ form, setForm, plans, idPrefix }) {
  return (
    <>
      <div className="space-y-1">
        <Label>Plan</Label>
        <Select value={form.plan_id} onValueChange={(v) => setForm((s) => ({ ...s, plan_id: v }))}>
          <SelectTrigger data-testid={`${idPrefix}-plan`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {(plans || []).map((pl) => <SelectItem key={pl.id} value={pl.id}>{planLabel(pl)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-email`}>Billing email</Label>
        <Input id={`${idPrefix}-email`} type="email" value={form.billing_email} onChange={(e) => setForm((s) => ({ ...s, billing_email: e.target.value }))} placeholder="ap@group.com" data-testid={`${idPrefix}-email`} />
        <p className="text-[11px] text-muted-foreground">Where Plan C invoices are sent — an invoice will not issue without one.</p>
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
        <div>
          <p className="text-sm">Tax exempt</p>
          <p className="text-[11px] text-muted-foreground">No sales tax on its invoices, so no permit or site rate is needed.</p>
        </div>
        <Switch checked={!!form.tax_exempt} onCheckedChange={(v) => setForm((s) => ({ ...s, tax_exempt: v }))} data-testid={`${idPrefix}-tax-exempt`} />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
        <p className="text-sm">Pays annually in advance</p>
        <Switch checked={!!form.annual_prepay} onCheckedChange={(v) => setForm((s) => ({ ...s, annual_prepay: v }))} data-testid={`${idPrefix}-annual-prepay`} />
      </div>
    </>
  );
}

function EditTermsDialog({ org, plans, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ plan_id: org.plan_id, billing_email: org.billing_email || '',
                tax_exempt: !!org.tax_exempt, annual_prepay: !!org.annual_prepay });
    }
  }, [open, org]);

  const save = async () => {
    // Only what changed — the audit row records exactly the terms that moved.
    const patch = {};
    if (form.plan_id !== org.plan_id) patch.plan_id = form.plan_id;
    if (form.billing_email.trim().toLowerCase() !== (org.billing_email || '')) patch.billing_email = form.billing_email.trim();
    if (form.tax_exempt !== !!org.tax_exempt) patch.tax_exempt = form.tax_exempt;
    if (form.annual_prepay !== !!org.annual_prepay) patch.annual_prepay = form.annual_prepay;
    if (!Object.keys(patch).length) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.patch(`/owner/organisations/${org.id}`, patch);
      toast.success(`${org.name} updated`);
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(errText(e, 'Could not update the organisation')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2" data-testid={`owner-org-edit-${org.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{org.name} — terms</DialogTitle>
          <DialogDescription>Applies to invoices built from now on. An issued invoice keeps the terms it was priced with.</DialogDescription>
        </DialogHeader>
        {form.plan_id !== undefined && (
          <div className="space-y-3"><TermsFields form={form} setForm={setForm} plans={plans} idPrefix="owner-org-edit" /></div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="owner-org-edit-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_VARIANT = { issued: 'default', draft: 'secondary', void: 'outline' };

/**
 * Stored Plan C invoices for one organisation — ADR 0003 blocker 3.
 *
 * Sits under the preview on purpose. The preview says what this month WOULD
 * cost at today's prices; an invoice says what we charged, at the prices that
 * were in force then, and never changes afterwards. Having both on one screen is
 * the cheapest way to keep that distinction.
 */
function InvoicePanel({ org, onChanged }) {
  const [invoices, setInvoices] = useState(null);
  const [period, setPeriod] = useState(lastClosedPeriod());
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState('');

  const load = React.useCallback(() => (
    api.get(`/owner/organisations/${org.id}/invoices`)
      .then((r) => setInvoices(r.data.invoices || []))
      .catch(() => setInvoices([]))
  ), [org.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      await load();
      onChanged?.();
      return r;
    } catch (e) { toast.error(errText(e, 'That did not work')); return null; }
    finally { setBusy(''); }
  };

  const build = () => act('build', async () => {
    const r = await api.post(`/owner/organisations/${org.id}/invoices`, { period });
    setDraft(r.data);
    return r;
  });

  const issue = (id) => act('issue', async () => {
    const r = await api.post(`/owner/invoices/${id}/issue`);
    toast.success(`Issued ${r.data.invoice.number} — it cannot change now`);
    setDraft(null);
    return r;
  });

  const send = (id) => act('send', async () => {
    const r = await api.post(`/owner/invoices/${id}/send`);
    toast.success(r.data.invoice.stripe?.already_issued
      ? 'Already with the customer — nothing was sent twice'
      : `${r.data.invoice.number} sent to the customer`);
    return r;
  });

  const correct = (row) => {
    const reason = window.prompt(
      `Why is ${row.number} being ${row.status === 'issued' && row.stripe?.sent_at ? 'credited' : 'corrected'}? `
      + 'This is read in a dispute.');
    if (!reason) return;
    // Sent invoices get a credit note; one that never left can simply be voided.
    const url = row.stripe?.sent_at
      ? `/owner/invoices/${row.id}/credit-note`
      : `/owner/invoices/${row.id}/void`;
    return act('correct', async () => {
      const r = await api.post(url, { reason });
      toast.success(r.data.invoice.kind === 'credit_note'
        ? `Credit note ${r.data.invoice.number} issued`
        : `${row.number} voided`);
      return r;
    });
  };

  const blocks = draft?.readiness || [];

  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="owner-org-invoices">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Invoices</p>
        <div className="flex items-center gap-1.5">
          <Input
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            className="h-8 w-28 text-xs"
            data-testid="owner-invoice-period"
          />
          <Button size="sm" variant="outline" onClick={build} disabled={busy === 'build'} data-testid="owner-invoice-build">
            {busy === 'build' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Build draft
          </Button>
        </div>
      </div>

      {draft && (
        <div className="rounded-md border bg-muted/40 p-3 space-y-2 text-xs">
          <div className="flex justify-between font-medium">
            <span>Draft for {draft.invoice.period_label}</span>
            <span className="tabular-nums">{money(draft.invoice.total_cents)}</span>
          </div>
          {draft.invoice.error && <p className="text-destructive">{draft.invoice.error}</p>}
          {blocks.length > 0 ? (
            <div className="space-y-1">
              {/* Never a bare "cannot issue": each line names the thing to go and
                  change, because the person reading it is the person who fixes it. */}
              {blocks.map((b) => (
                <p key={b.code} className="flex gap-1.5 text-amber-700 dark:text-amber-500">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{b.message}</span>
                </p>
              ))}
            </div>
          ) : (
            <Button size="sm" className="w-full" onClick={() => issue(draft.invoice.id)} disabled={busy === 'issue'} data-testid="owner-invoice-issue">
              {busy === 'issue' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
              Issue {money(draft.invoice.total_cents)} — this freezes it
            </Button>
          )}
        </div>
      )}

      {invoices === null && <Skeleton className="h-10" />}
      {invoices?.length === 0 && <p className="text-xs text-muted-foreground">Nothing invoiced yet.</p>}
      <div className="space-y-1.5">
        {(invoices || []).map((row) => (
          <div key={row.id} className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1.5 last:pb-0">
            <div className="min-w-0">
              <p className="font-medium truncate">
                {row.number || 'draft'}
                <span className="text-muted-foreground font-normal"> · {row.period_label}</span>
              </p>
              <p className="text-muted-foreground truncate">
                {row.kind === 'credit_note' ? 'Credit note' : `Priced at ${row.pricing_version || 'a snapshot'}`}
                {row.paid_at ? ' · paid' : row.stripe?.sent_at ? ' · sent' : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="tabular-nums">{money(row.total_cents)}</span>
              <Badge variant={STATUS_VARIANT[row.status] || 'outline'} className="text-[10px]">{row.status}</Badge>
              {row.status === 'issued' && row.kind === 'invoice' && !row.stripe?.sent_at && (
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => send(row.id)} disabled={busy === 'send'}>
                  <Send className="h-3 w-3" />
                </Button>
              )}
              {row.status === 'issued' && row.kind === 'invoice' && !row.corrected_by_invoice_id && (
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => correct(row)} disabled={busy === 'correct'}>
                  {row.stripe?.sent_at ? 'Credit' : 'Void'}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        An issued invoice stores the whole price list that produced it, so it re-prices to the same total
        for ever — a later price change cannot move it. Anything already sent to the customer is corrected
        with a credit note rather than edited.
      </p>
    </div>
  );
}

export default function OwnerOrganisations() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const EMPTY_FORM = { name: '', plan_id: 'standard', pricing_model: 'mode1', billing_email: '', tax_exempt: false, annual_prepay: false };
  const [form, setForm] = useState(EMPTY_FORM);
  const [quote, setQuote] = useState(null);      // the org whose preview is open
  const [quoteBusy, setQuoteBusy] = useState('');
  const [modelBusy, setModelBusy] = useState('');

  const load = () => api.get('/owner/organisations').then((r) => setData(r.data)).catch(() => setData({ organisations: [] }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.name.trim().length < 2) { toast.error('Organisation name is required'); return; }
    setCreating(true);
    try {
      const r = await api.post('/owner/organisations', form);
      toast.success(`${r.data.name} created — move its sites in from each practice page`);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(errText(e, 'Could not create organisation')); }
    finally { setCreating(false); }
  };

  // ADR 0004 decision 2: migrating a customer between pricing models is ONE
  // field. It is also the field that decides whether their patients are asked
  // for a card, so it saves on its own rather than inside a bigger form.
  const setPricingModel = async (org, pricing_model) => {
    if (pricing_model === (org.pricing_model || 'mode1')) return;
    setModelBusy(org.id);
    try {
      await api.patch(`/owner/organisations/${org.id}`, { pricing_model });
      toast.success(pricing_model === 'planc'
        ? `${org.name} is on Plan C — its patients are no longer charged by us`
        : `${org.name} is back on the per-document patient fee`);
      load();
    } catch (e) { toast.error(errText(e, 'Could not change the pricing model')); }
    finally { setModelBusy(''); }
  };

  const preview = async (org) => {
    setQuoteBusy(org.id);
    try {
      const r = await api.get(`/owner/organisations/${org.id}`);
      setQuote(r.data);
    } catch (e) { toast.error(errText(e, 'Could not price this organisation')); }
    finally { setQuoteBusy(''); }
  };

  if (!data) return <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-3"><Skeleton className="h-8 w-56" /><Skeleton className="h-64" /></div>;

  const orgs = data.organisations || [];
  const unassigned = data.unassigned_practices || [];

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-4" data-testid="owner-organisations-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organisations</h1>
          {/* The whole point, in one sentence a non-engineer can act on. */}
          <p className="text-sm text-muted-foreground max-w-3xl">
            An organisation is one <span className="font-medium">commercial customer</span>; a practice is one <span className="font-medium">site</span>.
            LeaveBridge Plan C assesses its submission ladder on the organisation's total across every site, so a group
            billed as separate practices is overcharged. Every practice starts in its own single-site organisation —
            group them here when they are one customer.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="owner-new-org-btn"><Plus className="h-4 w-4 mr-1" /> New organisation</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create an organisation</DialogTitle>
              <DialogDescription>Then open each practice that belongs to it and move the site across.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="org-name">Organisation name *</Label>
                <Input id="org-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Texas Oncology" data-testid="owner-org-name" />
              </div>
              <div className="space-y-1">
                <Label>Pricing model</Label>
                <Select value={form.pricing_model} onValueChange={(v) => setForm((s) => ({ ...s, pricing_model: v }))}>
                  <SelectTrigger data-testid="owner-org-pricing-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(data.pricing_models || []).map((pm) => (
                      <SelectItem key={pm.id} value={pm.id}>{pm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  On Plan C we never take the patient's card — the practice decides whether to charge one, and keeps it.
                </p>
              </div>
              <TermsFields form={form} setForm={setForm} plans={data.plans} idPrefix="owner-org" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={creating}>Cancel</Button>
              <Button onClick={create} disabled={creating} data-testid="owner-org-create">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Plan C is built and deliberately wired to no money path (ADR 0003). Saying
          so on the page it would otherwise be read off is cheaper than a surprise. */}
      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardContent className="p-3 text-xs text-amber-700 dark:text-amber-500 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Plan C's <span className="font-medium">prices are not in force</span> — everything priced here is a preview,
            and practices are still invoiced the live model ($100 base + seats). What a Plan C organisation
            <span className="font-medium"> does</span> change today is its patients: we never take their card
            (ADR 0004), and its submissions are counted for the ladder rather than charged per document.
          </span>
        </CardContent>
      </Card>

      {unassigned.length > 0 && (
        <Card className="border-destructive/40">
          <CardContent className="p-3 text-xs">
            <span className="font-medium">{unassigned.length} practice(s) not in any organisation:</span>{' '}
            {unassigned.map((u) => u.name).join(', ')} — reload this page to attach them.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {orgs.map((o) => (
          <Card key={o.id} data-testid={`owner-org-card-${o.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{o.name}</span>
                  </CardTitle>
                  {/* `capitalize` only on the plan id — applied to the whole line it
                      title-cases the billing email too ("Ap@Demohealth.Test"). */}
                  <CardDescription className="text-xs">
                    <span className="capitalize">{o.plan_id}</span> · {o.active_sites} active site{o.active_sites === 1 ? '' : 's'}
                    {o.sites_total !== o.active_sites && ` (${o.sites_total} total)`}
                    {o.billing_email ? ` · ${o.billing_email}` : ''}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {o.pricing_model === 'planc' && <Badge className="text-[10px]">Plan C</Badge>}
                  {o.is_demo && <Badge variant="secondary" className="text-[10px]">Demo</Badge>}
                  {o.tax_exempt && <Badge variant="outline" className="text-[10px]">Tax exempt</Badge>}
                  {(o.overrides || []).length > 0 && <Badge variant="outline" className="text-[10px]">{o.overrides.length} deal term(s)</Badge>}
                  <EditTermsDialog org={o} plans={data.plans} onSaved={load} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-sm">
                <span className="tabular-nums font-medium">{o.submissions_this_month}</span>
                <span className="text-muted-foreground"> submissions org-wide in {data.period_label}</span>
              </div>
              <div className="space-y-1">
                {(o.sites || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-xs gap-2">
                    <Link to={`/owner/practices/${s.id}`} className="truncate hover:underline">
                      {s.name}{s.disabled && <span className="text-muted-foreground"> · suspended</span>}
                    </Link>
                    <span className="tabular-nums text-muted-foreground shrink-0">{s.submissions_this_month}</span>
                  </div>
                ))}
                {(o.sites || []).length === 0 && <p className="text-xs text-muted-foreground">No sites yet.</p>}
              </div>
              {/* The one field that decides whether this customer's patients are
                  asked for a card (ADR 0004). Saves immediately — it is a
                  migration, not part of a draft. */}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground shrink-0">Pricing</span>
                <Select
                  value={o.pricing_model || 'mode1'}
                  onValueChange={(v) => setPricingModel(o, v)}
                  disabled={modelBusy === o.id}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid={`owner-org-model-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(data.pricing_models || []).map((pm) => (
                      <SelectItem key={pm.id} value={pm.id} className="text-xs">{pm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={() => preview(o)} disabled={quoteBusy === o.id} data-testid={`owner-org-preview-${o.id}`}>
                {quoteBusy === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Calculator className="h-3.5 w-3.5 mr-1" />}
                Preview Plan C for {data.period_label}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!quote} onOpenChange={(v) => !v && setQuote(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="owner-org-quote">
          <DialogHeader>
            <DialogTitle>{quote?.organisation?.name} — {quote?.basis?.period_label}</DialogTitle>
            <DialogDescription>
              {quote?.organisation?.plan_name} · {quote?.basis?.active_sites} active site(s) ·{' '}
              {quote?.basis?.total_submissions} submissions · {quote?.basis?.provider_seats} provider / {quote?.basis?.staff_seats} staff seats
            </DialogDescription>
          </DialogHeader>
          {quote?.error ? (
            <p className="text-sm text-destructive">{quote.error}</p>
          ) : quote?.quote ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                {quote.quote.lines.map((l) => (
                  <div key={l.key} className="flex justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">{l.label}{l.qty > 1 ? ` ×${l.qty}` : ''}</span>
                    <span className="tabular-nums">{money(l.amount_cents)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Total</span><span className="tabular-nums" data-testid="owner-org-quote-total">{money(quote.quote.total_cents)}</span>
                </div>
              </div>
              {/* The number that justifies the organisation existing at all. */}
              <div className="rounded-md border p-3 text-xs space-y-1">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Billed as separate practices</span>
                  <span className="tabular-nums">{money(quote.separately_billed_total_cents)}</span>
                </div>
                <div className="flex justify-between gap-3 font-medium">
                  <span>Saved by billing as one organisation</span>
                  <span className="tabular-nums">{money(quote.organisation_saving_cents)}</span>
                </div>
                <p className="text-muted-foreground pt-1">
                  One ladder across every site, and one pooled seat allowance — that difference is what the
                  organisation model exists to stop us overcharging.
                </p>
              </div>
              {(quote.expiring_overrides || []).length > 0 && (
                <div className="rounded-md border border-amber-500/40 p-3 text-xs space-y-1">
                  <p className="font-medium text-amber-700 dark:text-amber-500">Deal terms expiring soon</p>
                  {quote.expiring_overrides.map((e, i) => (
                    <p key={i} className="text-muted-foreground">{e.reason} — ends {e.effectiveTo} ({e.days_remaining} days)</p>
                  ))}
                </div>
              )}
              <ul className="text-[11px] text-muted-foreground list-disc pl-4 space-y-0.5">
                {(quote.caveats || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
              <InvoicePanel org={quote.organisation} onChanged={load} />
            </div>
          ) : null}
          {/* An organisation the engine cannot price still has invoices worth
              seeing — a past month that WAS priced, and the credit note that
              corrected it. So this sits outside the `quote?.quote` branch. */}
          {quote && !quote.quote && <InvoicePanel org={quote.organisation} onChanged={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
